/**
 * Role hierarchy sorting based on explicit role power.
 *
 * Each role possesses a numeric `power` (1 to 100), where higher power
 * indicates greater organizational authority, seniority, and approval rights.
 *
 * Zero hardcoded lists: any role (built-in or custom) sorts dynamically
 * based on its intrinsic power value.
 */

export const getRolePower = (role = {}) => {
  if (role.power != null && !isNaN(Number(role.power))) {
    return Number(role.power);
  }
  // Id-based fallback for system roles lacking explicit power field
  const id = String(role.id || "").toLowerCase();
  if (id === "super-admin") return 100;
  if (id === "admin") return 90;
  if (id === "it") return 80;
  if (id === "finance") return 75;
  if (id === "marketing") return 70;

  // Scope-based fallback for legacy roles lacking explicit power field
  const scope = String(role.scope || "").toLowerCase();
  if (role.isSystem || role.roleType === "system" || scope === "platform") return 85;
  if (scope === "management") return 70;
  if (scope === "corporate") return 50;
  if (scope === "resident") return 80;
  if (scope === "operations") return 30;
  return 50;
};

export const compareRolesByPower = (roleA = {}, roleB = {}) => {
  const powerA = getRolePower(roleA);
  const powerB = getRolePower(roleB);

  if (powerA !== powerB) {
    return powerB - powerA; // Descending: highest power first
  }

  // Tie-breaker: System roles first, then alphabetical by name
  if (Boolean(roleA.isSystem) !== Boolean(roleB.isSystem)) {
    return roleA.isSystem ? -1 : 1;
  }

  return String(roleA.name || "").localeCompare(String(roleB.name || ""));
};

export const sortRolesByHierarchy = (roles = []) => {
  if (!Array.isArray(roles)) return [];
  return [...roles].sort(compareRolesByPower);
};

export const compareRolesByHierarchy = compareRolesByPower;
